/**
 * Transposition: the same music, higher or lower.
 *
 * An interval, not a number of semitones, is what a musician transposes by. Up a minor
 * third from C is E flat and up an augmented second is D sharp; they sound the same and
 * are different notes on the page, and only the interval says which one was meant. Each
 * interval here therefore carries both of its measurements -- how many letters it moves
 * and how many semitones -- and the spelling follows from the pair.
 *
 * Key signatures move with the music when they are asked to. A piece in two flats
 * transposed up a tone is in two sharps, and leaving the signature behind would make every
 * note of it need an accidental.
 */

import { shiftHarmony } from "../document/harmony";
import type { Harmony } from "../document/harmony";
import { shiftPitch } from "../document/pitch";
import type { NotePitch } from "../document/pitch";
import { capacityOf, measureOnset, occupiedTicks } from "../document/query";
import type { EventLocation } from "../document/query";
import type { PartMeasure } from "../document/types";
import type { KeySignature, Score } from "../document/types";
import type { Command } from "../commands/types";
import { selectedLocations } from "./selection";
import type { Selection } from "./selection";

/** How far to move, in the two measurements notation keeps apart. */
export interface Interval {
  readonly name: string;
  /** Letters moved: a third moves two letters, an octave seven. */
  readonly steps: number;
  readonly semitones: number;
}

/** The intervals inside an octave, plus the octave itself. */
export const INTERVALS: readonly Interval[] = [
  { name: "Minor 2nd", steps: 1, semitones: 1 },
  { name: "Major 2nd", steps: 1, semitones: 2 },
  { name: "Minor 3rd", steps: 2, semitones: 3 },
  { name: "Major 3rd", steps: 2, semitones: 4 },
  { name: "Perfect 4th", steps: 3, semitones: 5 },
  { name: "Augmented 4th", steps: 3, semitones: 6 },
  { name: "Diminished 5th", steps: 4, semitones: 6 },
  { name: "Perfect 5th", steps: 4, semitones: 7 },
  { name: "Minor 6th", steps: 5, semitones: 8 },
  { name: "Major 6th", steps: 5, semitones: 9 },
  { name: "Minor 7th", steps: 6, semitones: 10 },
  { name: "Major 7th", steps: 6, semitones: 11 },
  { name: "Octave", steps: 7, semitones: 12 },
];

/** Up or down. */
export type Direction = 1 | -1;

function moved(pitch: NotePitch, interval: Interval, direction: Direction): NotePitch {
  // A drum is not a pitch: transposing a score leaves the kit where it was written, the
  // same way it leaves the words of a song where they were sung.
  if (pitch.drum !== null) return pitch;
  return shiftPitch(pitch, interval.steps * direction, interval.semitones * direction);
}

/**
 * A chord symbol moved with the music under it.
 *
 * A chord chart transposed without its chords is a chart in two keys, so the symbols go
 * wherever the notes go -- spelled by the same interval, which is why up a minor third from
 * C7 is E flat 7 and up an augmented second is D sharp 7.
 */
function movedHarmony(harmony: Harmony, interval: Interval, direction: Direction): Harmony {
  return shiftHarmony(harmony, interval.steps * direction, interval.semitones * direction);
}

/**
 * The key signature an interval away, around the circle of fifths.
 *
 * Seven fifths make a semitone, so a chromatic step is seven places around the circle; the
 * result is brought back inside seven sharps and seven flats, which is as far as a
 * signature is written.
 */
export function keyAfter(key: KeySignature, semitones: number): KeySignature {
  let fifths = key.fifths + semitones * 7;
  while (fifths > 7) fifths -= 12;
  while (fifths < -7) fifths += 12;
  return { ...key, fifths };
}

/** Move every selected note by an interval. */
export function transpose(
  score: Score,
  selection: Selection,
  interval: Interval,
  direction: Direction = 1,
): Command | string {
  const found = selectedLocations(score, selection).filter((at) => at.event.kind === "note");
  if (found.length === 0) return "no note is selected";
  const label = `${direction > 0 ? "up" : "down"} a ${interval.name.toLowerCase()}`;
  const notes = found.map((at) => ({
    kind: "setPitches" as const,
    eventId: at.event.id,
    pitches:
      at.event.kind === "note"
        ? at.event.pitches.map((pitch) => moved(pitch, interval, direction))
        : [],
  }));
  return {
    kind: "batch",
    label: `transpose ${label}`,
    // The bars go first and carry their music unchanged, so the notes are moved by the
    // `setPitches` after them rather than put back by a bar written from before the move.
    commands: [...harmonyCommands(score, found, interval, direction), ...notes],
  };
}

/**
 * The bars whose chord symbols stand over the selected notes, rewritten a step away.
 *
 * A symbol belongs to the music under it, so one is moved when a selected note is sounding
 * at the tick it stands on -- not only when a note begins there. Selecting four bars of a
 * chart and transposing them moves the chords with them; selecting one note in the middle
 * of a bar does not drag the chord that started on the beat before it.
 */
function harmonyCommands(
  score: Score,
  found: readonly EventLocation[],
  interval: Interval,
  direction: Direction,
): readonly Command[] {
  const bars = new Map<string, { at: EventLocation; ranges: [number, number][] }>();
  for (const at of found) {
    const start = at.onset - measureOnset(score, at.measureIndex);
    const key = `${at.partId}:${at.measureId}`;
    const entry = bars.get(key) ?? { at, ranges: [] };
    entry.ranges.push([start, start + occupiedTicks(at.event, capacityOf(score, at.measureIndex))]);
    bars.set(key, entry);
  }

  const commands: Command[] = [];
  for (const { at, ranges } of bars.values()) {
    const measure = measureOf(score, at);
    if (measure === null || measure.harmonies.length === 0) continue;
    let touched = false;
    const harmonies = measure.harmonies.map((harmony) => {
      const covered = ranges.some(([from, to]) => harmony.onset >= from && harmony.onset < to);
      if (!covered) return harmony;
      touched = true;
      return movedHarmony(harmony, interval, direction);
    });
    if (!touched) continue;
    commands.push({
      kind: "setPartMeasure",
      partId: at.partId,
      measureId: at.measureId,
      measure: { ...measure, harmonies },
    });
  }
  return commands;
}

function measureOf(score: Score, at: EventLocation): PartMeasure | null {
  return score.parts.find((part) => part.id === at.partId)?.measures[at.measureId] ?? null;
}

/**
 * Move the whole piece, signatures and all.
 *
 * Every note of every part goes, including parts that transpose already: their written
 * pitch is what is on the page, and the page is what is being moved. The signatures stated
 * anywhere in the piece move with it, so a modulation stays a modulation.
 */
export function transposeScore(
  score: Score,
  interval: Interval,
  direction: Direction = 1,
  withKeys = true,
): Command | string {
  const commands: Command[] = [];

  for (const part of score.parts) {
    for (const global of score.measures) {
      const measure = part.measures[global.id];
      if (measure === undefined) continue;
      const voices = measure.voices.map((voice) => ({
        ...voice,
        events: voice.events.map((event) =>
          event.kind === "note"
            ? { ...event, pitches: event.pitches.map((pitch) => moved(pitch, interval, direction)) }
            : event,
        ),
      }));
      const harmonies = measure.harmonies.map((harmony) =>
        movedHarmony(harmony, interval, direction),
      );
      commands.push({
        kind: "setPartMeasure",
        partId: part.id,
        measureId: global.id,
        measure: { ...measure, voices, harmonies },
      });
    }
  }

  if (withKeys) {
    for (const global of score.measures) {
      if (global.key === null) continue;
      commands.push({
        kind: "setMeasure",
        id: global.id,
        props: { key: keyAfter(global.key, interval.semitones * direction) },
      });
    }
  }

  if (commands.length === 0) return "there is nothing in the score to transpose";
  return { kind: "batch", label: "transpose the score", commands };
}
